import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import { createVendor, fetchChartOfAccounts, fetchVendors, updateVendor } from "../../../api/accounting";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { useLatestRef } from "./accountingUtils";

const EMPTY_FORM = {
  name: "",
  contact_email: "",
  contact_phone: "",
  payment_terms: "",
  default_account_id: "",
  tax_id: "",
  vat_number: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  country: "",
  contact_person_name: "",
  contact_person_title: "",
  contact_person_email: "",
  contact_person_phone: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_branch: "",
  notes: "",
};

const optional = (value) => {
  const trimmed = String(value ?? "").trim();
  return trimmed || null;
};

const formToPayload = (form, branchId) => ({
  branch_id: branchId || null,
  name: form.name.trim(),
  default_account_id: form.default_account_id || null,
  contact_email: optional(form.contact_email),
  contact_phone: optional(form.contact_phone),
  payment_terms: optional(form.payment_terms),
  tax_id: optional(form.tax_id),
  vat_number: optional(form.vat_number),
  address_line1: optional(form.address_line1),
  address_line2: optional(form.address_line2),
  city: optional(form.city),
  region: optional(form.region),
  postal_code: optional(form.postal_code),
  country: optional(form.country),
  contact_person_name: optional(form.contact_person_name),
  contact_person_title: optional(form.contact_person_title),
  contact_person_email: optional(form.contact_person_email),
  contact_person_phone: optional(form.contact_person_phone),
  bank_name: optional(form.bank_name),
  bank_account_name: optional(form.bank_account_name),
  bank_account_number: optional(form.bank_account_number),
  bank_branch: optional(form.bank_branch),
  notes: optional(form.notes),
});

const vendorToForm = (row) => ({
  name: row.name || "",
  contact_email: row.contact_email || "",
  contact_phone: row.contact_phone || "",
  payment_terms: row.payment_terms || "",
  default_account_id: row.default_account_id || "",
  tax_id: row.tax_id || "",
  vat_number: row.vat_number || "",
  address_line1: row.address_line1 || "",
  address_line2: row.address_line2 || "",
  city: row.city || "",
  region: row.region || "",
  postal_code: row.postal_code || "",
  country: row.country || "",
  contact_person_name: row.contact_person_name || "",
  contact_person_title: row.contact_person_title || "",
  contact_person_email: row.contact_person_email || "",
  contact_person_phone: row.contact_person_phone || "",
  bank_name: row.bank_name || "",
  bank_account_name: row.bank_account_name || "",
  bank_account_number: row.bank_account_number || "",
  bank_branch: row.bank_branch || "",
  notes: row.notes || "",
});

const SectionHeading = ({ title, hint }) => (
  <div className="mb-2 mt-3 pt-2 border-top">
    <h6 className="mb-0">{title}</h6>
    {hint ? <small className="text-muted">{hint}</small> : null}
  </div>
);

const VendorsPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(
      () =>
        Promise.all([
          fetchVendors(branchId || undefined).then(setRows),
          fetchChartOfAccounts({ branch_id: branchId || undefined, account_type: "liability", active_only: true }).then(
            setAccounts
          ),
        ]),
      { errorMessage: "Failed to load vendors." }
    )
      .catch((err) => setError(formatApiError(err, "Failed to load vendors.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const setActive = useCallback(
    (row, isActive) => {
      confirm({
        title: isActive ? `Reactivate ${row.name}?` : `Deactivate ${row.name}?`,
        confirmLabel: isActive ? "Reactivate" : "Deactivate",
        tone: isActive ? "primary" : "danger",
        successMessage: isActive ? "Vendor reactivated." : "Vendor deactivated.",
        onConfirm: async () => {
          await updateVendor(row.id, { is_active: isActive });
          await loadRef.current();
        },
      });
    },
    [confirm]
  );

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm(vendorToForm(row));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const columns = useMemo(
    () => [
      { name: "Name", selector: (r) => r.name, sortable: true, grow: 2 },
      { name: "Contact", selector: (r) => r.contact_person_name || r.contact_email || "—", width: "160px" },
      { name: "TIN", selector: (r) => r.tax_id || "—", width: "110px" },
      { name: "VAT", selector: (r) => r.vat_number || "—", width: "110px" },
      { name: "Phone", selector: (r) => r.contact_phone || r.contact_person_phone || "—", width: "130px" },
      { name: "Terms", selector: (r) => r.payment_terms || "—", width: "100px" },
      { name: "AP Account", selector: (r) => r.default_account_name || "—", width: "150px" },
      {
        name: "Status",
        cell: (r) => <Badge color={r.is_active ? "success" : "secondary"}>{r.is_active ? "Active" : "Inactive"}</Badge>,
        width: "90px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            onEdit={() => openEdit(r)}
            editLabel="Edit vendor"
            actions={[
              {
                icon: r.is_active ? "clear" : "plus",
                label: r.is_active ? "Deactivate vendor" : "Activate vendor",
                onClick: () => setActive(r, !r.is_active),
                variant: r.is_active ? "danger" : "primary",
              },
            ]}
          />
        ),
        width: "88px",
        ignoreRowClick: true,
      },
    ],
    [setActive]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = formToPayload(form, branchId);
      if (editing) {
        await runCrud(() => updateVendor(editing.id, payload), { successMessage: "Vendor updated." });
      } else {
        await runCrud(() => createVendor(payload), { successMessage: "Vendor created." });
      }
      closeModal();
      load();
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Vendors" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Vendor register</h5>
            <div className="d-flex gap-2">
              <Input type="select" bsSize="sm" style={{ width: "180px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={openCreate}>
                New vendor
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={closeModal} size="xl">
          <Form onSubmit={handleSubmit}>
            <ModalHeader toggle={closeModal}>{editing ? "Edit vendor" : "New vendor"}</ModalHeader>
            <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <SectionHeading title="Basic details" />
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Vendor name</Label>
                    <Input required value={form.name} onChange={setField("name")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Company email</Label>
                    <Input type="email" value={form.contact_email} onChange={setField("contact_email")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Company phone</Label>
                    <Input value={form.contact_phone} onChange={setField("contact_phone")} />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeading title="Contact person" hint="Optional" />
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Full name</Label>
                    <Input value={form.contact_person_name} onChange={setField("contact_person_name")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Job title</Label>
                    <Input value={form.contact_person_title} onChange={setField("contact_person_title")} placeholder="Accounts payable" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Email</Label>
                    <Input type="email" value={form.contact_person_email} onChange={setField("contact_person_email")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Phone</Label>
                    <Input value={form.contact_person_phone} onChange={setField("contact_person_phone")} />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeading title="Address" hint="Optional" />
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label>Address line 1</Label>
                    <Input value={form.address_line1} onChange={setField("address_line1")} />
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Address line 2</Label>
                    <Input value={form.address_line2} onChange={setField("address_line2")} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>City</Label>
                    <Input value={form.city} onChange={setField("city")} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Region</Label>
                    <Input value={form.region} onChange={setField("region")} />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Postal code</Label>
                    <Input value={form.postal_code} onChange={setField("postal_code")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Country</Label>
                    <Input value={form.country} onChange={setField("country")} placeholder="Tanzania" />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeading title="Tax & VAT" hint="Optional" />
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>TIN / Tax ID</Label>
                    <Input value={form.tax_id} onChange={setField("tax_id")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>VAT number</Label>
                    <Input value={form.vat_number} onChange={setField("vat_number")} />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeading title="Bank account" hint="Optional" />
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Bank name</Label>
                    <Input value={form.bank_name} onChange={setField("bank_name")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Branch</Label>
                    <Input value={form.bank_branch} onChange={setField("bank_branch")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Account name</Label>
                    <Input value={form.bank_account_name} onChange={setField("bank_account_name")} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Account number</Label>
                    <Input value={form.bank_account_number} onChange={setField("bank_account_number")} />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeading title="Accounting" hint="Optional" />
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Payment terms</Label>
                    <Input value={form.payment_terms} onChange={setField("payment_terms")} placeholder="Net 30" />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>Default AP account</Label>
                    <Input type="select" value={form.default_account_id} onChange={setField("default_account_id")}>
                      <option value="">None</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.code} — {a.name}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md="12">
                  <FormGroup>
                    <Label>Notes</Label>
                    <Input type="textarea" rows={3} value={form.notes} onChange={setField("notes")} />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Saving…" : editing ? "Save changes" : "Create vendor"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default VendorsPage;
